import { useEffect } from "react";
import { SITE } from "@/config/site";

/**
 * Sets page title, meta description and Open Graph / Twitter card tags on mount.
 * CRA doesn't give us SSR; this is the pragmatic client-side fallback until the
 * site is migrated to Next.js or react-snap pre-render runs.
 */
export default function PageMeta({ title, description, image, url }) {
  useEffect(() => {
    if (title) document.title = title;

    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.key).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content);
    };

    if (description) {
      upsert('meta[name="description"]', { key: { name: "description" }, content: description });
      upsert('meta[property="og:description"]', { key: { property: "og:description" }, content: description });
      upsert('meta[name="twitter:description"]', { key: { name: "twitter:description" }, content: description });
    }
    if (title) {
      upsert('meta[property="og:title"]', { key: { property: "og:title" }, content: title });
      upsert('meta[name="twitter:title"]', { key: { name: "twitter:title" }, content: title });
    }
    const defaultImg = image || "https://ascentremit.com/og-image.png";
    upsert('meta[property="og:image"]', { key: { property: "og:image" }, content: defaultImg });
    upsert('meta[name="twitter:image"]', { key: { name: "twitter:image" }, content: defaultImg });
    upsert('meta[property="og:type"]', { key: { property: "og:type" }, content: "website" });
    upsert('meta[name="twitter:card"]', { key: { name: "twitter:card" }, content: "summary_large_image" });

    // og:url — always built against the canonical SITE_URL so pre-rendered
    // pages don't bake in the prerender's localhost origin.
    const base = (SITE.SITE_URL || "").replace(/\/$/, "");
    const pathname =
      url ||
      (typeof window !== "undefined"
        ? (window.location.pathname.replace(/\/$/, "") || "/")
        : "/");
    const pageUrl = url && /^https?:\/\//.test(url) ? url : `${base}${pathname}`;
    if (pageUrl) {
      upsert('meta[property="og:url"]', { key: { property: "og:url" }, content: pageUrl });
      let canonical = document.head.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", pageUrl);
    }
  }, [title, description, image, url]);

  return null;
}
